// Shared quote → invoice conversion — single source of truth so the "Convert"
// action behaves IDENTICALLY whether triggered from the document preview
// (ViewDocument) or the edit screen (EditDocument): same invoice-number
// reservation, same new-invoice document, and same quote-marking. Callers own
// their own confirm dialog, success alert, and post-convert navigation so each
// screen keeps its existing UX.
//
// Inventory: under the manual model, conversion does NOT deduct stock and does
// NOT prompt. The new invoice is created with inventoryStatus:'pending'; the
// user deducts later via the Mark-as-Deducted action (which drives the ledger).

import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { generateDocNumber } from './docNumber';
import { paymentMirrorFields } from './paymentStatus';

// Convert a saved quote into a new invoice document. `quoteData` is the FULL,
// canonical quote (as loaded from Firestore, including its id/docNumber/etc.);
// callers editing a quote should persist their edits and re-read the quote first
// so the invoice reflects the latest state. Returns { invoiceId, invoiceNumber }.
export async function convertQuoteToInvoice({ quoteId, quoteData, isAccount }) {
  const invoiceNumber = await generateDocNumber('invoice');
  const now = new Date().toISOString();

  const { id: _id, ...quoteFields } = quoteData;
  const newInvoiceRef = await addDoc(collection(db, 'invoices'), {
    ...quoteFields,
    docNumber: invoiceNumber,
    docType: 'invoice',
    convertedFromQuote: quoteData.docNumber,
    status: 'Ordered',
    statusUpdatedAt: now,
    // A derived mirror, not truth (see utils/paymentStatus.js). The new invoice's
    // id does not exist until this write, so the payments collection holds
    // nothing for it: the mirror is derived from exactly that — no records.
    ...paymentMirrorFields(quoteData.grandTotal, []),
    payments: [],
    createdAt: now,
    date: now,
    // Carry the resolved account status forward so the new invoice labels correctly
    // (snapshot on the quote if present, else the live-lookup result; default regular).
    customerAccount: isAccount,
    // Manual inventory model: the invoice starts pending — no stock moved here.
    // The user deducts later via Mark-as-Deducted. No deductedInventory ledger yet.
    inventoryStatus: 'pending',
  });

  await updateDoc(doc(db, 'quotes', quoteId), {
    convertedToInvoice: invoiceNumber,
    // Also store the new invoice's ID so callers can link straight to it (the
    // number alone would require a lookup). Legacy quotes lack this field.
    convertedToInvoiceId: newInvoiceRef.id,
  });
  return { invoiceId: newInvoiceRef.id, invoiceNumber };
}
